import { useEffect, useState } from "react";
import { useUser } from "./hooks/useUser";
import { usePlayerState } from "./hooks/usePlayerState";
import { identifyTester, tagActiveMode } from "./sentry";
import { MODE_BY_ID } from "./state/modeConfig";
import { hasOnboarded, markOnboarded } from "./state/persistence";
import { MainMenu } from "./screens/MainMenu";
import { Onboarding } from "./screens/Onboarding";
import { DigitSpan } from "./modes/DigitSpan";
import { SpatialRecall } from "./modes/SpatialRecall";
import { DualTask } from "./modes/DualTask";
import { NBack } from "./modes/NBack";
import { GoNoGo } from "./modes/GoNoGo";
import { RuleSwitch } from "./modes/RuleSwitch";
import { ChunkingChallenge } from "./modes/ChunkingChallenge";
import { useTranslation } from "./i18n/useTranslation";

export default function App() {
  const { user } = useUser();
  const player = usePlayerState(user);
  const [activeMode, setActiveMode] = useState<string | null>(null);
  const [showOnboarding, setShowOnboarding] = useState(() => !hasOnboarded(user));
  const { t } = useTranslation();

  useEffect(() => {
    identifyTester(user);
  }, [user]);

  useEffect(() => {
    tagActiveMode(activeMode);
  }, [activeMode]);

  if (showOnboarding) {
    return (
      <Onboarding
        onDone={() => {
          markOnboarded(user);
          setShowOnboarding(false);
        }}
      />
    );
  }

  if (activeMode === null) {
    return (
      <MainMenu
        user={user}
        player={player}
        onStartMode={(modeId) => setActiveMode(modeId)}
      />
    );
  }

  const config = MODE_BY_ID[activeMode];
  if (!config) {
    setActiveMode(null);
    return null;
  }

  // Show a "coming soon" placeholder for any future mode that isn't yet wired.
  // All 7 current modes have implementations below.
  const onExit = () => setActiveMode(null);

  switch (activeMode) {
    case "digit_span":
      return <DigitSpan key={activeMode} config={config} player={player} onExit={onExit} />;
    case "spatial":
      return <SpatialRecall key={activeMode} config={config} player={player} onExit={onExit} />;
    case "dual_task":
      return <DualTask key={activeMode} config={config} player={player} onExit={onExit} />;
    case "nback":
      return <NBack key={activeMode} config={config} player={player} onExit={onExit} />;
    case "go_nogo":
      return <GoNoGo key={activeMode} config={config} player={player} onExit={onExit} />;
    case "rule_switch":
      return <RuleSwitch key={activeMode} config={config} player={player} onExit={onExit} />;
    case "chunking":
      return <ChunkingChallenge key={activeMode} config={config} player={player} onExit={onExit} />;
    default:
      return (
        <main className="min-h-screen flex flex-col items-center justify-center px-6 text-center">
          <h1 className="text-4xl mb-4">{t(`mode.${activeMode}.name`)}</h1>
          <button type="button" onClick={onExit} className="text-cyan">
            ← {t("common.back")}
          </button>
        </main>
      );
  }
}
